/**
 * Pure H OS · Sprint 1 · Executive Dashboard.
 *
 * The master screen. Every future screen inherits from the primitives
 * used here (Card, SectionHeader, DeltaChip, SeverityBadge,
 * ConfidenceBar, ScoreDial). Sections are pure functions of the
 * mocked payload — swapping to a real API is a single-file diff.
 */
import { usePureH } from "../context/PureHContext";
import {
  EXECUTIVE_DASHBOARD_DATA as D,
} from "../mocks/data";
import {
  Card, SectionHeader, SeverityBadge,
  ConfidenceBar, ScoreDial,
} from "../components/lib";
import { CanonicalKpiCell } from "../components/CanonicalKpiCell";
import { useCanonicalKpi } from "../hooks/useCanonicalKpi";
import { EmptyState } from "../components/EmptyState";
import {
  Sparkles, Sunrise, AlertOctagon, GavelIcon, Activity,
  Lightbulb, ClipboardCheck, Building2, Layers,
} from "lucide-react";

// ── Executive Context ──────────────────────────────────────────
function ExecutiveContext() {
  const { user, currentCompany, currentWorkspace } = usePureH();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const headline = D.contextHeadline?.trim() || "";
  const subline  = D.contextSubline?.trim()  || "";
  return (
    <div className="mb-8" data-testid="section-executive-context">
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Executive Context</div>
      <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ letterSpacing: "-0.02em" }}>
        {greeting}, {user?.name}.
      </h1>
      <div className="mt-1 text-sm text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          <Building2 size={13} className="text-slate-400" />
          {currentCompany?.name}
        </span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-1.5">
          <Layers size={13} className="text-slate-400" />
          {currentWorkspace?.name}
        </span>
        {headline && <><span className="text-slate-300">·</span><span>{headline}</span></>}
      </div>
      {subline && <div className="mt-1 text-sm text-slate-500">{subline}</div>}
    </div>
  );
}

// ── Performance Snapshot ────────────────────────────────────────
function PerformanceSnapshot() {
  const { data: kpi, loading, error } = useCanonicalKpi("orvan");
  return (
    <Card testId="section-performance">
      <SectionHeader eyebrow="Snapshot" title="Performance"
        action={<span className="text-[10px] uppercase tracking-[0.16em] text-slate-500" data-testid="kpi-source-tag">Source: /api/pureh/kpi/summary</span>} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <CanonicalKpiCell
          testId="kpi-revenue"
          label="Revenue (MTD)"
          value={loading ? null : (kpi?.revenue.monthlyEGP.value ?? 0)}
          unit="EGP"
          source={kpi?.revenue.sourceCollection || "pureh_customer_payments"}
          footnote={error ? "endpoint unavailable" : (kpi?.dataMode === "demo" ? "demo mode" : undefined)}
          empty={loading}
        />
        <CanonicalKpiCell
          testId="kpi-orders-30d"
          label="Orders · 30d"
          value={loading ? null : (kpi?.orders.last30d.value ?? 0)}
          unit={null}
          source={kpi?.orders.sourceCollection || "pureh_orders"}
        />
        <CanonicalKpiCell
          testId="kpi-live-skus"
          label="Live SKUs"
          value={loading ? null : (kpi?.products.liveSkus.value ?? 0)}
          unit={null}
          source={kpi?.products.sourceCollection || "pureh_product_master"}
        />
        <CanonicalKpiCell
          testId="kpi-launches"
          label="Launches in flight"
          value={loading ? null : (kpi?.products.launchesInFlight.value ?? 0)}
          unit={null}
          source={kpi?.products.sourceCollection || "pureh_product_master"}
        />
        <CanonicalKpiCell
          testId="kpi-approvals"
          label="Approvals · mine"
          value={loading ? null : (kpi?.approvals.assignedToMe.value ?? 0)}
          unit={null}
          source={kpi?.approvals.sourceCollection || "pureh_approvals"}
        />
        <CanonicalKpiCell
          testId="kpi-qc-pass"
          label="QC pass · 90d"
          value={loading ? null : kpi?.qc.passRate90d.value}
          unit="%"
          source={kpi?.qc.sourceCollection || "pureh_qc_checks"}
        />
      </div>
    </Card>
  );
}

// ── Morning Brief ───────────────────────────────────────────────
function MorningBrief() {
  const tagStyles: Record<string, { fg: string; bg: string; label: string }> = {
    risk:        { fg: "var(--ph-danger)",  bg: "var(--ph-danger-tint)",  label: "Risk"        },
    opportunity: { fg: "var(--ph-success)", bg: "var(--ph-success-tint)", label: "Opportunity" },
    insight:     { fg: "var(--ph-warning)", bg: "var(--ph-warning-tint)", label: "Insight"     },
  };
  const items = D.morningBrief || [];
  return (
    <Card testId="section-morning-brief">
      <SectionHeader
        eyebrow="Start here"
        title="Morning Brief"
        action={<Sunrise size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-morning-brief-empty" title="No brief items yet."
          hint="AI will publish daily risks, opportunities, and insights once your workspace has activity." />
      ) : (
        <div className="space-y-4">
          {items.map((m) => {
            const s = tagStyles[m.tag];
            return (
              <div key={m.id} data-testid={`brief-${m.id}`} className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-[0.14em] flex-shrink-0"
                    style={{ background: s.bg, color: s.fg }}
                  >
                    {s.label}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{m.headline}</div>
                    <div className="text-xs text-slate-600 mt-1 leading-relaxed">{m.body}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Requires Attention ─────────────────────────────────────────
function RequiresAttention() {
  const items = D.attention || [];
  return (
    <Card testId="section-attention">
      <SectionHeader
        eyebrow="Now"
        title="Requires Attention"
        action={<AlertOctagon size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-attention-empty" title="Nothing needs attention."
          hint="QC overdues, expiring licences, stock alerts, and approvals will appear here." />
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((a) => (
            <div key={a.id} data-testid={`attention-${a.id}`} className="py-3 first:pt-0 last:pb-0 flex items-start gap-3">
              <SeverityBadge severity={a.severity} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{a.detail}</div>
              </div>
              <div className="text-[11px] text-slate-500 flex-shrink-0 tabular-nums">{a.dueRelative}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Pending Decisions ──────────────────────────────────────────
function PendingDecisions() {
  const items = D.decisions || [];
  return (
    <Card testId="section-decisions">
      <SectionHeader
        eyebrow="Decide"
        title="Pending Decisions"
        action={<GavelIcon size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-decisions-empty" title="No pending decisions."
          hint="Decisions submitted for owner approval will appear here." />
      ) : (
        <div className="space-y-4">
          {items.map((d) => (
            <div key={d.id} data-testid={`decision-${d.id}`} className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
              <div className="text-sm font-semibold text-slate-900">{d.title}</div>
              <div className="text-xs text-slate-600 mt-1">{d.context}</div>
              <div className="flex items-center gap-3 mt-2 text-[11px]">
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <ClipboardCheck size={11} /> {d.owner}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">by {d.requiredBy}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Department Health ──────────────────────────────────────────
function DepartmentHealth() {
  const items = D.departments || [];
  return (
    <Card testId="section-departments">
      <SectionHeader eyebrow="Pulse" title="Department Health" />
      {items.length === 0 ? (
        <EmptyState testId="section-departments-empty" title="No department scores yet."
          hint="Health scores compute from real activity — batches, sales, campaigns, approvals." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((dept) => (
            <div key={dept.key} data-testid={`dept-${dept.key}`}
              className="flex items-center gap-3 p-3 border border-slate-100 rounded">
              <ScoreDial score={dept.score} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{dept.name}</div>
                <div className="text-[11px] text-slate-500 truncate mt-0.5">{dept.headline}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Revenue Trend (SVG minimal chart) ──────────────────────────
function RevenueTrend() {
  const revenueData = D.revenue || [];
  if (revenueData.length === 0) {
    return (
      <Card testId="section-revenue">
        <SectionHeader eyebrow="Financial" title="Revenue Trend" />
        <EmptyState testId="section-revenue-empty" title="No revenue data yet." hint="Trend will appear once you have posted invoices with received payments." />
      </Card>
    );
  }
  const w = 640;
  const h = 200;
  const pad = 32;
  const values = revenueData.map((p) => p.revenue).concat(revenueData.map((p) => p.target));
  const max = Math.max(...values);
  const min = Math.min(...values);
  const spanY = Math.max(0.001, max - min);
  const x = (i: number) => pad + (i * (w - 2 * pad)) / Math.max(1, revenueData.length - 1);
  const y = (v: number) => h - pad - ((v - min) / spanY) * (h - 2 * pad);

  const revenuePath = revenueData.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.revenue)}`).join(" ");
  const targetPath  = revenueData.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.target)}`).join(" ");
  const latest = revenueData[revenueData.length - 1];

  return (
    <Card testId="section-revenue">
      <SectionHeader
        eyebrow="Financial"
        title="Revenue Trend"
        action={
          <div className="flex items-center gap-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-0.5 rounded" style={{ background: "var(--ph-brand-primary)" }} /> Actual</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-0.5 rounded" style={{ background: "var(--ph-brand-accent)" }} /> Target</span>
          </div>
        }
      />
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" aria-hidden="true">
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad} x2={w - pad} y1={pad + t * (h - 2 * pad)} y2={pad + t * (h - 2 * pad)}
            stroke="var(--ph-border-subtle)" strokeWidth={1} strokeDasharray="2 4" />
        ))}
        <path d={targetPath}  fill="none" stroke="var(--ph-brand-accent)" strokeWidth={1.5} strokeDasharray="4 4" />
        <path d={revenuePath} fill="none" stroke="var(--ph-brand-primary)" strokeWidth={2} />
        {revenueData.map((p, i) => (
          <g key={p.month}>
            <circle cx={x(i)} cy={y(p.revenue)} r={3} fill="var(--ph-brand-primary)" />
            <text x={x(i)} y={h - 8} textAnchor="middle" fontSize={10} fill="var(--ph-ink-soft)">{p.month}</text>
          </g>
        ))}
      </svg>
      <div className="mt-2 grid grid-cols-3 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Latest</div>
          <div className="text-lg font-semibold text-slate-900 tabular-nums">EGP {latest.revenue.toFixed(2)}M</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Target</div>
          <div className="text-lg font-semibold text-slate-900 tabular-nums">EGP {latest.target.toFixed(2)}M</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Vs Target</div>
          <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>
            {latest.target > 0 ? `${(((latest.revenue - latest.target) / latest.target) * 100).toFixed(1)}%` : "—"}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── AI Discoveries ─────────────────────────────────────────────
function AiDiscoveries() {
  const items = D.discoveries || [];
  return (
    <Card testId="section-ai-discoveries">
      <SectionHeader
        eyebrow="AI"
        title="Discoveries"
        action={<Sparkles size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-ai-discoveries-empty" title="No AI discoveries yet."
          hint="Patterns and insights will surface here once activity flows through your workspace." />
      ) : (
        <div className="space-y-4">
          {items.map((d) => (
            <div key={d.id} data-testid={`disc-${d.id}`} className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
              <div className="text-sm font-semibold text-slate-900">{d.title}</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">{d.body}</div>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1"><ConfidenceBar value={d.confidence} /></div>
                <div className="text-[11px] tabular-nums text-slate-500">{Math.round(d.confidence * 100)}% confidence</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── AI Recommendations ────────────────────────────────────────
function AiRecommendations() {
  const items = D.recommendations || [];
  return (
    <Card testId="section-ai-recommendations">
      <SectionHeader
        eyebrow="AI"
        title="Recommendations"
        action={<Lightbulb size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-ai-recommendations-empty" title="No recommendations yet."
          hint="AI will suggest actions once it has enough operational context to reason about." />
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`rec-${r.id}`} className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-600 mt-1">{r.action}</div>
                  <div className="text-[11px] text-slate-500 mt-1"><span className="font-medium text-slate-700">Impact:</span> {r.impact}</div>
                </div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded flex-shrink-0"
                  style={{ background: "var(--ph-brand-tint)", color: "var(--ph-brand-primary)" }}
                  title={`Effort: ${r.effort}`}
                >{r.effort}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Activity Timeline ─────────────────────────────────────────
function ActivityTimeline() {
  const dot: Record<string, string> = {
    ai:     "var(--ph-ai)",
    human:  "var(--ph-success)",
    system: "var(--ph-ink-soft)",
  };
  const items = D.activity || [];
  return (
    <Card testId="section-activity">
      <SectionHeader
        eyebrow="History"
        title="Activity Timeline"
        action={<Activity size={14} className="text-slate-400" />}
      />
      {items.length === 0 ? (
        <EmptyState testId="section-activity-empty" title="No activity yet."
          hint="System, human, and AI actions will appear here in chronological order." />
      ) : (
        <ul className="relative">
          <span className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" aria-hidden="true" />
          {items.map((e) => (
            <li key={e.id} data-testid={`act-${e.id}`} className="relative pl-6 pb-4 last:pb-0">
              <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-white flex items-center justify-center"
                style={{ background: dot[e.channel] }} />
              <div className="text-sm text-slate-900">
                <span className="font-semibold">{e.actor}</span> <span className="text-slate-600">{e.verb}</span>{" "}
                <span className="text-slate-800">{e.target}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{e.timestamp}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Composed page ─────────────────────────────────────────────
export default function ExecutiveDashboard() {
  return (
    <div data-testid="pureh-executive-dashboard">
      <ExecutiveContext />
      <PerformanceSnapshot />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <MorningBrief />
          <RevenueTrend />
          <DepartmentHealth />
        </div>
        <div className="space-y-6">
          <RequiresAttention />
          <PendingDecisions />
          <AiDiscoveries />
          <AiRecommendations />
        </div>
      </div>

      <div className="mt-6">
        <ActivityTimeline />
      </div>
    </div>
  );
}
